import type { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccountBalanceIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import { AMTLICHE_MELDUNG_POST_CATEGORY } from '@/features/post-editor/constants/postCategory';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { PillToggle } from '@/shared/components/PillToggle';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

/**
 * "Amtliche Meldungen" toggle, bound to the post's `categories` field.
 * Switching it on sets the language-neutral
 * {@link AMTLICHE_MELDUNG_POST_CATEGORY} value; switching it off clears the
 * category. Only rendered on the `NEWS_ITEM` channel (guarded by the caller)
 */
export const PostAmtlicheMeldungSwitch: FC = () => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor.organization'
  });
  const { control } = useFormContext<PostFormValues>();

  return (
    <Controller
      control={control}
      name="categories"
      render={({ field }) => {
        const isOfficial = field.value === AMTLICHE_MELDUNG_POST_CATEGORY;
        const setOfficial = (on: boolean) =>
          field.onChange(on ? AMTLICHE_MELDUNG_POST_CATEGORY : undefined);
        return (
          <ListItem
            title={t('officialLabel')}
            icon={() => (
              <AccountBalanceIcon
                fill={theme.colors.onSurfaceVariant}
                width={24}
                height={24}
              />
            )}
            arrowIcon={() => (
              <PillToggle value={isOfficial} onValueChange={setOfficial} />
            )}
            onPress={() => setOfficial(!isOfficial)}
          />
        );
      }}
    />
  );
};
